/**
 * Experiment metrics dashboard section (#27). Mounted inside
 * `ExperimentDetailPage.tsx` after the variants table.
 *
 * Layout:
 *   - Hidden when the experiment is DRAFT (no data yet).
 *   - Loading skeleton while the metrics query is pending.
 *   - Error alert with the server-side message if the fetch fails.
 *   - Per-variant table on md+ / card list on xs (mirrors the variants
 *     section's responsive split).
 *   - Each row: variant name, weight, assignments, page-views, goal
 *     event count, conversion %, sparkline of last 7 days of
 *     assignments.
 *   - Totals row at the bottom of the table.
 *
 * MUI-only — no Recharts / chart libs. The sparkline is a small inline
 * SVG built directly from the `dailyAssignments` array. Lightweight and
 * keeps the bundle small; a full chart layer is overkill for one
 * 7-bucket bar series per row.
 */

import type { ReactElement } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { ExperimentMetricsResponse, ExperimentStatus } from "@ottopilot/shared";
import { useExperimentMetrics } from "../../hooks/useExperiments";

export type ExperimentMetricsSectionProps = {
  experimentId: string;
  status: ExperimentStatus;
};

/**
 * Format a 0..1 conversion rate as a percent string with one decimal.
 * Centralised so the totals row + per-variant rows render identically.
 */
function formatPercent(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

/**
 * Render a tiny inline SVG bar chart from a 7-day series. Bars are
 * normalised to the max value in the series (or 1 to avoid /0). The
 * SVG is kept absolutely small so it sits inside a table cell without
 * breaking layout — 80×24 px.
 */
function Sparkline({
  series,
  ariaLabel,
}: {
  series: ReadonlyArray<{ date: string; count: number }>;
  ariaLabel: string;
}): ReactElement {
  const width = 80;
  const height = 24;
  const max = Math.max(1, ...series.map((p) => p.count));
  const barWidth = series.length > 0 ? width / series.length : 0;
  return (
    <Box
      component="svg"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={ariaLabel}
      data-testid="metrics-sparkline"
      sx={{ display: "inline-block", verticalAlign: "middle" }}
    >
      {series.map((point, i) => {
        const h = max === 0 ? 0 : (point.count / max) * (height - 2);
        return (
          <rect
            key={point.date}
            x={i * barWidth + 1}
            y={height - h}
            width={Math.max(1, barWidth - 2)}
            height={h}
            fill="currentColor"
            opacity={point.count === 0 ? 0.15 : 0.8}
          >
            <title>{`${point.date}: ${point.count}`}</title>
          </rect>
        );
      })}
    </Box>
  );
}

function MetricsTable({ data }: { data: ExperimentMetricsResponse }): ReactElement {
  return (
    <Paper variant="outlined" sx={{ display: { xs: "none", md: "block" } }}>
      <TableContainer>
        <Table aria-label="Experiment metrics" data-testid="experiment-metrics-table">
          <TableHead>
            <TableRow>
              <TableCell>Variant</TableCell>
              <TableCell align="right">Weight (%)</TableCell>
              <TableCell align="right">Assignments</TableCell>
              <TableCell align="right">Page views</TableCell>
              <TableCell align="right">
                Goal {data.goalEvent ? `(${data.goalEvent})` : "(none)"}
              </TableCell>
              <TableCell align="right">Conversion</TableCell>
              <TableCell align="right">Last 7 days</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.variants.map((v) => (
              <TableRow
                key={v.variantId}
                data-testid="experiment-metrics-row"
                data-variant-id={v.variantId}
              >
                <TableCell>{v.name}</TableCell>
                <TableCell align="right">{v.weight}</TableCell>
                <TableCell align="right" data-testid="metrics-assignments">
                  {v.assignments}
                </TableCell>
                <TableCell align="right" data-testid="metrics-page-views">
                  {v.pageViews}
                </TableCell>
                <TableCell align="right" data-testid="metrics-goal-events">
                  {v.goalEventCount}
                </TableCell>
                <TableCell align="right" data-testid="metrics-conversion">
                  {formatPercent(v.conversionRate)}
                </TableCell>
                <TableCell align="right">
                  <Sparkline
                    series={v.dailyAssignments}
                    ariaLabel={`${v.name} daily assignments, last 7 days`}
                  />
                </TableCell>
              </TableRow>
            ))}
            <TableRow data-testid="experiment-metrics-totals">
              <TableCell sx={{ fontWeight: 500 }}>Totals</TableCell>
              <TableCell align="right">—</TableCell>
              <TableCell align="right" sx={{ fontWeight: 500 }}>
                {data.totals.assignments}
              </TableCell>
              <TableCell align="right">—</TableCell>
              <TableCell align="right" sx={{ fontWeight: 500 }}>
                {data.totals.goalEventCount}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 500 }}>
                {formatPercent(data.totals.conversionRate)}
              </TableCell>
              <TableCell align="right">—</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

function MetricsCardList({ data }: { data: ExperimentMetricsResponse }): ReactElement {
  return (
    <Stack
      spacing={1.5}
      sx={{ display: { xs: "flex", md: "none" } }}
      data-testid="experiment-metrics-cards"
    >
      {data.variants.map((v) => (
        <Card key={v.variantId} variant="outlined">
          <CardContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              sx={{ mb: 1 }}
            >
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {v.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {v.weight}%
              </Typography>
            </Stack>
            <Stack direction="row" spacing={3} flexWrap="wrap" sx={{ mb: 1 }}>
              <Stack>
                <Typography variant="caption" color="text.secondary">
                  Assignments
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {v.assignments}
                </Typography>
              </Stack>
              <Stack>
                <Typography variant="caption" color="text.secondary">
                  Page views
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {v.pageViews}
                </Typography>
              </Stack>
              <Stack>
                <Typography variant="caption" color="text.secondary">
                  Goal {data.goalEvent ?? "(none)"}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {v.goalEventCount}
                </Typography>
              </Stack>
              <Stack>
                <Typography variant="caption" color="text.secondary">
                  Conversion
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatPercent(v.conversionRate)}
                </Typography>
              </Stack>
            </Stack>
            <Sparkline
              series={v.dailyAssignments}
              ariaLabel={`${v.name} daily assignments, last 7 days`}
            />
          </CardContent>
        </Card>
      ))}
      <Card variant="outlined" data-testid="experiment-metrics-totals-card">
        <CardContent>
          <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
            Totals
          </Typography>
          <Stack direction="row" spacing={3} flexWrap="wrap">
            <Stack>
              <Typography variant="caption" color="text.secondary">
                Assignments
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {data.totals.assignments}
              </Typography>
            </Stack>
            <Stack>
              <Typography variant="caption" color="text.secondary">
                Goal events
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {data.totals.goalEventCount}
              </Typography>
            </Stack>
            <Stack>
              <Typography variant="caption" color="text.secondary">
                Conversion
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {formatPercent(data.totals.conversionRate)}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}

export function ExperimentMetricsSection({
  experimentId,
  status,
}: ExperimentMetricsSectionProps): ReactElement | null {
  // Hide entirely while the experiment is in DRAFT — there are no
  // assignments yet, so the dashboard would render an empty table that
  // doesn't tell the operator anything useful. Issue #27 brief.
  const enabled = status !== "DRAFT";
  const metrics = useExperimentMetrics(experimentId, enabled);

  if (!enabled) return null;

  return (
    <Box data-testid="experiment-metrics-section">
      <Divider sx={{ my: 2 }} />
      <Typography variant="h3" sx={{ mb: 2 }}>
        Metrics
      </Typography>

      {metrics.isPending ? (
        <Stack
          spacing={1}
          sx={{ alignItems: "center", py: 4 }}
          data-testid="experiment-metrics-loading"
        >
          <CircularProgress size={24} />
          <Skeleton variant="rectangular" width="100%" height={48} />
          <Skeleton variant="rectangular" width="100%" height={32} />
          <Skeleton variant="rectangular" width="100%" height={32} />
        </Stack>
      ) : metrics.isError ? (
        <Alert severity="error" data-testid="experiment-metrics-error">
          {metrics.error.message || "Could not load metrics."}
        </Alert>
      ) : (
        <>
          <MetricsTable data={metrics.data} />
          <MetricsCardList data={metrics.data} />
        </>
      )}
    </Box>
  );
}
